/**
 * Sticky "Start designing" bar (mobile only).
 *
 * The real "Start designing" button is rendered by the tidal-colors-app app block,
 * so the theme can't render it directly. Instead we watch the real button with an
 * IntersectionObserver (same pattern as sticky-atc-bar.js) and, while it is out of
 * view, show a fixed bottom bar. Tapping the bar forwards the click to the real app
 * button, which opens the app's design popup.
 *
 * The bar only activates when the app block is present (default product template),
 * so DTF / other templates are skipped automatically.
 */
(function () {
  'use strict';

  var MOBILE_MQ =
    (window.FoxTheme && FoxTheme.config && FoxTheme.config.mediaQueryMobile) ||
    'screen and (max-width: 767px)';
  var BLOCK_SELECTOR = '[data-app-block-id*="start_designing_button"]';
  var LABEL = 'Start designing';

  var state = {
    bar: null,
    realWrap: null,
    realBtn: null,
    observer: null,
    mql: null,
    reserved: false,
  };

  function findReal() {
    var wrap = document.querySelector(BLOCK_SELECTOR);
    if (!wrap) return null;
    var btn = wrap.querySelector('button, a, [role="button"]');
    return { wrap: wrap, btn: btn || wrap };
  }

  function buildBar(realBtn) {
    var bar = document.createElement('div');
    bar.className = 'sticky-design-bar';
    bar.setAttribute('aria-hidden', 'true');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sticky-design-bar__btn';
    btn.textContent = LABEL;

    var arrow = document.createElement('span');
    arrow.className = 'sticky-design-bar__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = '&rarr;';
    btn.appendChild(arrow);

    // Match the real CTA colour when we can read it (kept slightly transparent),
    // else fall back to the brand orange defined in CSS.
    try {
      var cs = window.getComputedStyle(realBtn);
      var bg = cs.backgroundColor;
      if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
        var m = bg.match(/rgba?\(([^)]+)\)/);
        if (m) {
          var p = m[1].split(',');
          btn.style.backgroundColor =
            'rgba(' + p[0].trim() + ',' + p[1].trim() + ',' + p[2].trim() + ',0.8)';
        }
        btn.style.color = cs.color;
      }
    } catch (e) {}

    btn.addEventListener('click', function () {
      if (state.realBtn) state.realBtn.click();
    });

    bar.appendChild(btn);
    document.body.appendChild(bar);
    return bar;
  }

  function show() {
    if (!state.bar) return;
    state.bar.classList.add('sticky-design-bar--show');
    state.bar.setAttribute('aria-hidden', 'false');
  }

  function hide() {
    if (!state.bar) return;
    state.bar.classList.remove('sticky-design-bar--show');
    state.bar.setAttribute('aria-hidden', 'true');
  }

  // Reserve a constant amount of space so the page bottom is never hidden under the
  // bar and so toggling the bar (transform only) never reflows / oscillates the observer.
  function reserveSpace(on) {
    if (on && !state.reserved && state.bar) {
      document.documentElement.style.setProperty(
        '--sticky-design-bar-height',
        state.bar.offsetHeight + 'px'
      );
      document.body.classList.add('has-sticky-design-bar');
      state.reserved = true;
    } else if (!on && state.reserved) {
      document.body.classList.remove('has-sticky-design-bar');
      state.reserved = false;
    }
  }

  function startObserver() {
    if (state.observer || !state.realWrap) return;
    state.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) hide();
          else show();
        });
      },
      // Exclude the bottom strip (where the bar sits) so the swap happens before the
      // real button would slide under the bar.
      { root: null, threshold: 0, rootMargin: '0px 0px -84px 0px' }
    );
    state.observer.observe(state.realWrap);
  }

  function stopObserver() {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
  }

  function enable() {
    var real = findReal();
    if (!real) return false;
    state.realWrap = real.wrap;
    state.realBtn = real.btn;
    if (!state.bar) state.bar = buildBar(state.realBtn);
    reserveSpace(true);
    startObserver();
    return true;
  }

  function disable() {
    stopObserver();
    hide();
    reserveSpace(false);
    if (state.bar) {
      state.bar.remove();
      state.bar = null;
    }
    state.realWrap = null;
    state.realBtn = null;
  }

  function evaluate() {
    if (state.mql && state.mql.matches) enable();
    else disable();
  }

  function init() {
    state.mql = window.matchMedia(MOBILE_MQ);

    // The app button may be injected after our script runs; wait for it briefly.
    if (!findReal()) {
      var mo = new MutationObserver(function () {
        if (findReal()) {
          mo.disconnect();
          evaluate();
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () {
        mo.disconnect();
      }, 10000);
    }

    evaluate();

    if (state.mql.addEventListener) state.mql.addEventListener('change', evaluate);
    else if (state.mql.addListener) state.mql.addListener(evaluate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
